import { useEffect, useMemo } from "react";

import { PERSONAL_INFORMATION } from "@jti/fe-common";
import { useAvpAvailableMethods } from "@jti/rest-api";

import { getAgeEstimationVisibleMethods } from "../../utils/ageEstimationPolicy";

interface UseSetDefaultValidationMethodProps {
  selectedMethod: string;
  selectMethod: (selectedMethod: string) => void;
  isAgeEstimationFlowEnabled?: boolean;
  authorizedAgeEstimationMethodCode?: string;
}

export type AgeVerificationMethodType = ReturnType<typeof useAvpAvailableMethods>["availableMethods"][number];

export interface UseAvMethodsReturn {
  isAvailableMethodsLoading: boolean;
  availableMethods: AgeVerificationMethodType[];
  allAvailableMethods: AgeVerificationMethodType[];
  method?: AgeVerificationMethodType;
  onSelectMethod: (methodCode: string) => void;
}

export function useAvMethods({
  selectedMethod,
  selectMethod,
  isAgeEstimationFlowEnabled,
  authorizedAgeEstimationMethodCode = "",
}: UseSetDefaultValidationMethodProps): UseAvMethodsReturn {
  const { availableMethods: allAvailableMethods, isAvailableMethodsLoading } = useAvpAvailableMethods();

  const availableMethods = useMemo(
    () =>
      isAgeEstimationFlowEnabled
        ? getAgeEstimationVisibleMethods(allAvailableMethods, authorizedAgeEstimationMethodCode)
        : allAvailableMethods,
    [allAvailableMethods, authorizedAgeEstimationMethodCode, isAgeEstimationFlowEnabled]
  );

  useEffect(() => {
    if (!availableMethods?.length || availableMethods.some(({ code }) => code === selectedMethod)) {
      return;
    }

    // Take first method other then personal information
    const defaultSelectedMethodItem = availableMethods.find((methodItem) => methodItem.code !== PERSONAL_INFORMATION);

    if (defaultSelectedMethodItem) {
      selectMethod(defaultSelectedMethodItem.code);
    } else {
      // If there is no other methods available, then select the first one (personal information)
      selectMethod(availableMethods[0].code);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMethods]);

  const method = useMemo(() => {
    return availableMethods?.find((methodItem) => methodItem.code === selectedMethod);
  }, [availableMethods, selectedMethod]);

  const onSelectMethod = (methodCode: string) => {
    const selectedMethodItem = availableMethods?.find((methodItem) => methodItem.code === methodCode);
    selectMethod(selectedMethodItem?.code ?? "");
  };

  return { allAvailableMethods, availableMethods, isAvailableMethodsLoading, method, onSelectMethod };
}
